import { useLayoutEffect, useRef } from 'react'

import { useChannelSubscription } from '../../../Centrifuge'
import { isEmailMessageCompletedEvent } from '../../Email.utils'

/**
 * Subscribe to an email realtime channel and react to `message_completed`
 * events (a message finished sending — successfully or with an error).
 *
 * Callers supply `onMessageCompleted(payload)` to patch their own data (a
 * message in a thread detail, or a row in a list), an optional `onResync`
 * to close gaps after a dropped socket (fired only when a live subscription
 * to the same channel reconnects — a re-enable after a deliberate
 * unsubscribe counts as initial again), and an optional `onInitialSubscribe`
 * fired when the first subscription to a channel is established — the chance
 * to catch a completion that landed between loading the data and the socket
 * becoming ready. A channel change starts over with `onInitialSubscribe`: the
 * new channel's data arrives via its own load flow, and a resync would race it
 * (e.g. reloading an email list mid account switch, before its filter
 * params exist).
 *
 * @param {{
 *   channel: string | null,
 *   enabled?: boolean,
 *   onInitialSubscribe?: () => void,
 *   onMessageCompleted: (payload: {
 *     completed: boolean,
 *     uuid: string,
 *     thread_uuid: string,
 *     error?: { code: number | null, is_persistent: boolean, title: string | null },
 *     files?: Array<{ id: number, name: string, mime_type: string }>,
 *   }) => void,
 *   onResync?: () => void,
 * }} options
 */
export function useEmailMessageCompletedRealtime({
  channel,
  enabled = true,
  onInitialSubscribe,
  onMessageCompleted,
  onResync,
}) {
  const onInitialSubscribeRef = useRef(onInitialSubscribe)
  const onMessageCompletedRef = useRef(onMessageCompleted)
  const onResyncRef = useRef(onResync)
  const hasSubscribedRef = useRef(false)

  useLayoutEffect(() => {
    onInitialSubscribeRef.current = onInitialSubscribe
    onMessageCompletedRef.current = onMessageCompleted
    onResyncRef.current = onResync
  }, [onInitialSubscribe, onMessageCompleted, onResync])

  // Runs before useChannelSubscription resubscribes (layout vs passive
  // effect), so the new channel's first `subscribed` event takes the
  // initial-subscribe branch instead of resync.
  useLayoutEffect(() => {
    hasSubscribedRef.current = false
  }, [channel])

  // A deliberate unsubscribe (`enabled` off) is not a dropped socket: there is
  // no gap to resync, the consumer remounts and loads its data from scratch —
  // so the next subscribe must take the initial-subscribe branch again.
  useLayoutEffect(() => {
    if (!enabled) {
      hasSubscribedRef.current = false
    }
  }, [enabled])

  function handlePublication(ctx) {
    const payload = ctx?.data?.payload

    if (isEmailMessageCompletedEvent(ctx) && payload?.uuid) {
      onMessageCompletedRef.current(payload)
    }
  }

  // Skip the first subscribe per channel; only resync on a reconnect of the
  // same channel to recover a completion that may have fired while the
  // socket was down.
  function handleSubscribed() {
    if (hasSubscribedRef.current) {
      onResyncRef.current?.()
    } else {
      hasSubscribedRef.current = true

      onInitialSubscribeRef.current?.()
    }
  }

  useChannelSubscription({
    channel,
    enabled,
    onPublication: handlePublication,
    onSubscribed: handleSubscribed,
  })
}
